import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import SNMPOidHelper from '../components/SNMPOidHelper';
import api from '../utils/api';
import { toast } from 'sonner';
import { Activity, Plus, Loader2, Trash2, RefreshCw, CheckCircle2, XCircle, AlertCircle, Shield, Search, Filter, Edit } from 'lucide-react';

const UptimeMonitor = () => {
  const navigate = useNavigate();
  const [monitors, setMonitors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [monitorToDelete, setMonitorToDelete] = useState(null);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClientId, setFilterClientId] = useState('all');
  
  const [name, setName] = useState('');
  const [monitoringType, setMonitoringType] = useState('icmp');
  const [target, setTarget] = useState('');
  const [ports, setPorts] = useState('');
  const [clientId, setClientId] = useState('none');
  const [checkInterval, setCheckInterval] = useState(60);
  
  // SNMP specific fields
  const [snmpVersion, setSnmpVersion] = useState('2c');
  const [snmpCommunity, setSnmpCommunity] = useState('public');
  const [snmpPort, setSnmpPort] = useState('161');
  const [snmpOid, setSnmpOid] = useState('1.3.6.1.2.1.1.3.0'); // sysUpTime OID

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [monitorsRes, clientsRes] = await Promise.all([
        api.get('/monitors'),
        api.get('/clients')
      ]);
      setMonitors(monitorsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMonitor = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const monitorData = {
        name,
        monitoring_type: monitoringType,
        target,
        client_id: clientId && clientId !== 'none' ? clientId : undefined,
        check_interval: checkInterval
      };

      // Add type-specific fields
      if (monitoringType === 'tcp') {
        monitorData.ports = ports;
      } else if (monitoringType === 'snmp') {
        monitorData.snmp_version = snmpVersion;
        monitorData.snmp_community = snmpCommunity;
        monitorData.snmp_port = parseInt(snmpPort);
        monitorData.snmp_oid = snmpOid;
      }

      await api.post('/monitors', monitorData);
      toast.success('Monitor added!');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add monitor');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckMonitor = async (monitorId) => {
    setChecking(prev => ({ ...prev, [monitorId]: true }));
    
    try {
      await api.post(`/monitors/${monitorId}/check`);
      toast.success('Check completed!');
      fetchData();
    } catch (error) {
      toast.error('Check failed');
    } finally {
      setChecking(prev => ({ ...prev, [monitorId]: false }));
    }
  };

  const handleDeleteMonitor = async () => {
    if (!monitorToDelete) return;

    try {
      await api.delete(`/monitors/${monitorToDelete.id}`);
      toast.success('Monitor deleted!');
      setDeleteDialogOpen(false);
      setMonitorToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEditClick = (monitor) => {
    setEditingMonitor(monitor);
    setName(monitor.name);
    setMonitoringType(monitor.monitoring_type);
    setTarget(monitor.target);
    setPorts(monitor.ports || '');
    setClientId(monitor.client_id || 'none');
    setCheckInterval(monitor.check_interval);
    
    // Set SNMP fields if it's an SNMP monitor
    if (monitor.monitoring_type === 'snmp') {
      setSnmpVersion(monitor.snmp_version || '2c');
      setSnmpCommunity(monitor.snmp_community || 'public');
      setSnmpPort(monitor.snmp_port?.toString() || '161');
      setSnmpOid(monitor.snmp_oid || '1.3.6.1.2.1.1.3.0');
    }
    
    setShowEditDialog(true);
  };

  const handleEditMonitor = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const monitorData = {
        name,
        monitoring_type: monitoringType,
        target,
        client_id: clientId && clientId !== 'none' ? clientId : undefined,
        check_interval: checkInterval
      };

      // Add type-specific fields
      if (monitoringType === 'tcp') {
        monitorData.ports = ports;
      } else if (monitoringType === 'snmp') {
        monitorData.snmp_version = snmpVersion;
        monitorData.snmp_community = snmpCommunity;
        monitorData.snmp_port = parseInt(snmpPort);
        monitorData.snmp_oid = snmpOid;
      }

      await api.put(`/monitors/${editingMonitor.id}`, monitorData);
      toast.success('Monitor updated!');
      setShowEditDialog(false);
      setEditingMonitor(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update monitor');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setName('');
    setMonitoringType('icmp');
    setTarget('');
    setPorts('');
    setClientId('none');
    setCheckInterval(60);
    // Reset SNMP fields
    setSnmpVersion('2c');
    setSnmpCommunity('public');
    setSnmpPort('161');
    setSnmpOid('1.3.6.1.2.1.1.3.0');
  };

  const getStatusIcon = (status) => {
    if (status === 'up') return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (status === 'down') return <XCircle className="w-5 h-5 text-red-600" />;
    return <AlertCircle className="w-5 h-5 text-yellow-600" />;
  };

  const getSSLBadgeColor = (daysRemaining) => {
    if (!daysRemaining) return 'bg-slate-100 text-slate-600';
    if (daysRemaining > 180) return 'bg-green-100 text-green-700 border-green-200';
    if (daysRemaining > 30) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.company_name : 'No Client';
  };

  const getFilteredMonitors = () => {
    return monitors.filter(monitor => {
      // Search filter
      const matchesSearch = !searchTerm || 
        monitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        monitor.target.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Client filter
      const matchesClient = filterClientId === 'all' || 
        (filterClientId === 'none' && !monitor.client_id) ||
        monitor.client_id === filterClientId;
      
      return matchesSearch && matchesClient;
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          {/* Header Card */}
          <Card className="mb-6 shadow-sm border-slate-200">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Uptime Monitoring</h1>
                  <p className="text-slate-600 mt-1">Monitor devices and services</p>
                </div>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-md w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Monitor
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Total</p>
                    <p className="text-xl md:text-2xl font-bold text-slate-800">{monitors.length}</p>
                  </div>
                  <Activity className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Up</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">
                      {monitors.filter(m => m.status === 'up').length}
                    </p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Down</p>
                    <p className="text-xl md:text-2xl font-bold text-red-600">
                      {monitors.filter(m => m.status === 'down').length}
                    </p>
                  </div>
                  <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Avg Uptime</p>
                    <p className="text-xl md:text-2xl font-bold text-slate-800">
                      {monitors.length > 0
                        ? `${(monitors.reduce((acc, m) => acc + (m.uptime_percentage || 100), 0) / monitors.length).toFixed(1)}%`
                        : '100%'}
                    </p>
                  </div>
                  <Activity className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filter Bar */}
          {monitors.length > 0 && (
            <div className="mb-6 flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by device name or target..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10"
                />
              </div>
              <div className="w-64 relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
                <Select value={filterClientId} onValueChange={setFilterClientId}>
                  <SelectTrigger className="pl-10">
                    <SelectValue placeholder="Filter by company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    <SelectItem value="none">No Company</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {monitors.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Activity className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No monitors yet</h3>
              <p className="text-slate-500 mb-6">Start monitoring your devices</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Monitor
              </Button>
            </CardContent>
          </Card>
        ) : getFilteredMonitors().length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Activity className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No monitors match your filters</h3>
              <p className="text-slate-500 mb-6">Try adjusting your search or filter criteria</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterClientId('all'); }}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {getFilteredMonitors().map((monitor) => (
              <Card key={monitor.id} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className="mt-1 flex-shrink-0">{getStatusIcon(monitor.status)}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 
                            className="text-base sm:text-lg font-semibold text-slate-800 hover:text-blue-600 cursor-pointer transition-colors truncate"
                            onClick={() => navigate(`/uptime/${monitor.id}`)}
                          >
                            {monitor.name}
                          </h3>
                          <Badge variant="outline" className="uppercase text-xs flex-shrink-0">{monitor.monitoring_type}</Badge>
                          {monitor.client_id && (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs flex-shrink-0">
                              {getClientName(monitor.client_id)}
                            </Badge>
                          )}
                        </div>

                        <p className="text-sm text-slate-600 mb-3 break-all">{monitor.target}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Response</p>
                            <p className="text-sm font-semibold text-slate-700">
                              {monitor.response_time ? `${monitor.response_time}ms` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Avg Response</p>
                            <p className="text-sm font-semibold text-slate-700">
                              {monitor.avg_response_time ? `${monitor.avg_response_time}ms` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Uptime</p>
                            <p className="text-sm font-semibold text-green-600">
                              {monitor.uptime_percentage?.toFixed(1) || '100'}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Last Check</p>
                            <p className="text-sm font-semibold text-slate-700">
                              {monitor.last_check ? new Date(monitor.last_check).toLocaleTimeString() : 'Never'}
                            </p>
                          </div>
                        </div>

                        {monitor.monitoring_type === 'http' && monitor.ssl_days_remaining !== null && (
                          <div className="mt-3 flex items-center space-x-2">
                            <Shield className="w-4 h-4 text-slate-500" />
                            <Badge className={getSSLBadgeColor(monitor.ssl_days_remaining)}>
                              SSL: {monitor.ssl_days_remaining} days
                            </Badge>
                          </div>
                        )}

                        {monitor.open_ports && monitor.open_ports.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mb-1">Open Ports:</p>
                            <div className="flex flex-wrap gap-1">
                              {monitor.open_ports.slice(0, 10).map(port => (
                                <Badge key={port} variant="outline" className="text-xs">{port}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex lg:flex-col items-center lg:items-end gap-2 lg:gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCheckMonitor(monitor.id)}
                        disabled={checking[monitor.id]}
                        className="border-blue-300 text-blue-700 flex-1 lg:flex-initial lg:w-full"
                        title="Check Now"
                      >
                        {checking[monitor.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        <span className="ml-1 lg:hidden">Check</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditClick(monitor)}
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 flex-1 lg:flex-initial lg:w-full"
                        title="Edit Monitor"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="ml-1 lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setMonitorToDelete(monitor); setDeleteDialogOpen(true); }}
                        className="border-red-200 text-red-600 flex-1 lg:flex-initial lg:w-full"
                        title="Delete Monitor"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="ml-1 lg:hidden">Delete</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Monitor</DialogTitle>
            <DialogDescription>Configure monitoring</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMonitor}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input placeholder="Server Name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={monitoringType} onValueChange={setMonitoringType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="icmp">ICMP (Ping)</SelectItem>
                      <SelectItem value="http">HTTP/HTTPS</SelectItem>
                      <SelectItem value="tcp">TCP Port</SelectItem>
                      <SelectItem value="snmp">SNMP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Target *</Label>
                <Input placeholder="192.168.1.1 or example.com" value={target} onChange={(e) => setTarget(e.target.value)} required />
              </div>

              {monitoringType === 'tcp' && (
                <div className="space-y-2">
                  <Label>Ports *</Label>
                  <Input placeholder="80, 443, 3306" value={ports} onChange={(e) => setPorts(e.target.value)} required />
                </div>
              )}
              {monitoringType === 'snmp' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SNMP Version *</Label>
                      <Select value={snmpVersion} onValueChange={setSnmpVersion}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">v1</SelectItem>
                          <SelectItem value="2c">v2c</SelectItem>
                          <SelectItem value="3">v3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Community String *</Label>
                      <Input placeholder="public" value={snmpCommunity} onChange={(e) => setSnmpCommunity(e.target.value)} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SNMP Port *</Label>
                      <Input type="number" placeholder="161" value={snmpPort} onChange={(e) => setSnmpPort(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>OID *</Label>
                      <Input placeholder="1.3.6.1.2.1.1.3.0" value={snmpOid} onChange={(e) => setSnmpOid(e.target.value)} required />
                    </div>
                  </div>
                  <SNMPOidHelper onSelectOid={(oid) => setSnmpOid(oid)} />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client (Optional)</Label>
                  <Select value={clientId || "none"} onValueChange={(val) => setClientId(val === "none" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Client</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Interval (sec)</Label>
                  <Input type="number" min="30" value={checkInterval} onChange={(e) => setCheckInterval(parseInt(e.target.value))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? 'Adding...' : 'Add Monitor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Monitor</DialogTitle>
            <DialogDescription>Update monitor configuration</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditMonitor}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input placeholder="Server Name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={monitoringType} onValueChange={setMonitoringType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="icmp">ICMP (Ping)</SelectItem>
                      <SelectItem value="http">HTTP/HTTPS</SelectItem>
                      <SelectItem value="tcp">TCP Port</SelectItem>
                      <SelectItem value="snmp">SNMP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Target *</Label>
                <Input placeholder="192.168.1.1 or example.com" value={target} onChange={(e) => setTarget(e.target.value)} required />
              </div>

              {monitoringType === 'tcp' && (
                <div className="space-y-2">
                  <Label>Ports *</Label>
                  <Input placeholder="80, 443, 3306" value={ports} onChange={(e) => setPorts(e.target.value)} required />
                </div>
              )}
              {monitoringType === 'snmp' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SNMP Version *</Label>
                      <Select value={snmpVersion} onValueChange={setSnmpVersion}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">v1</SelectItem>
                          <SelectItem value="2c">v2c</SelectItem>
                          <SelectItem value="3">v3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Community String *</Label>
                      <Input placeholder="public" value={snmpCommunity} onChange={(e) => setSnmpCommunity(e.target.value)} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SNMP Port *</Label>
                      <Input type="number" placeholder="161" value={snmpPort} onChange={(e) => setSnmpPort(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>OID *</Label>
                      <Input placeholder="1.3.6.1.2.1.1.3.0" value={snmpOid} onChange={(e) => setSnmpOid(e.target.value)} required />
                    </div>
                  </div>
                  <SNMPOidHelper onSelectOid={(oid) => setSnmpOid(oid)} />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client (Optional)</Label>
                  <Select value={clientId || "none"} onValueChange={(val) => setClientId(val === "none" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Client</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Interval (sec)</Label>
                  <Input type="number" min="30" value={checkInterval} onChange={(e) => setCheckInterval(parseInt(e.target.value))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditDialog(false); setEditingMonitor(null); resetForm(); }} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Updating...' : 'Update Monitor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Monitor</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <strong>{monitorToDelete?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMonitor} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UptimeMonitor;
